
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  serverTimestamp,
  FirestoreError,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ActivityLog } from '@/models/LearnTypes';
import { toast } from '@/lib/toast';

class ActivityService {
  private collection = 'activityLogs';

  // Log a new activity (level or lesson completion)
  async logActivity(activity: Omit<ActivityLog, 'id' | 'timestamp'>): Promise<string | null> {
    try {
      if (!activity.userId) {
        console.error('Error logging activity: No user ID provided');
        return null;
      }

      // CRITICAL FIX: Ensure perfectLessonBonus is properly set and added to xpEarned
      if (activity.type === 'lesson-completed' && activity.isPerfectLesson === true) {
        // Default to 10 if not specified but the lesson was perfect
        if (activity.perfectLessonBonus === undefined || activity.perfectLessonBonus === 0) {
          console.warn('Perfect lesson without bonus specified, defaulting to 10');
          activity.perfectLessonBonus = 10;
        }
        
        // IMPORTANT FIX: Calculate the correct total XP by adding the bonus to baseXpEarned
        if (activity.baseXpEarned && activity.perfectLessonBonus) {
          // Store the original XP values for debugging
          const originalXpEarned = activity.xpEarned;
          const baseXp = activity.baseXpEarned;
          const bonus = activity.perfectLessonBonus;
          
          // Update xpEarned to be the sum of base + bonus
          activity.xpEarned = baseXp + bonus;
          
          console.log(`📝 [ActivityService] Updating xpEarned for perfect lesson: ${baseXp} base + ${bonus} bonus = ${activity.xpEarned} total (was: ${originalXpEarned})`);
        }
        
        console.log(`📝 [ActivityService] Logging perfect lesson with bonus: ${activity.perfectLessonBonus}, total XP: ${activity.xpEarned}`);
      }
      
      // CRITICAL FIX: Comprehensive validation for perfect status in all level completions
      if (activity.type === 'level-completed') {
        // Special logging for spelling exercises
        if (activity.levelType === 'spelling') {
          console.log(`🔍 [ActivityService] Validating spelling exercise perfect status: isPerfect=${activity.isPerfect}, levelType=${activity.levelType}`);
        } else {
          console.log(`🔍 [ActivityService] Validating level perfect status: isPerfect=${activity.isPerfect}, levelType=${activity.levelType || 'unknown'}`);
        }
        
        // Double-check all sources of spelling mistakes
        let spellingHasMistake = false;
        try {
          // Check global flags
          if ((window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ === true) {
            spellingHasMistake = true;
            console.warn('⚠️ [ActivityService] Global flag indicates spelling mistake');
          }
          
          // Check localStorage for mistake flags
          const levelIndex = activity.levelIndex;
          if (
            localStorage.getItem('spelling_exercise_had_mistake') === 'true' ||
            (levelIndex !== undefined && localStorage.getItem(`spelling_exercise_had_mistake_level_${levelIndex}`) === 'true')
          ) {
            spellingHasMistake = true;
            console.warn(`⚠️ [ActivityService] localStorage indicates spelling mistake for level ${levelIndex}`);
          }
          
          // If mistakes were detected but activity says perfect, override it
          if (spellingHasMistake && activity.isPerfect === true) {
            console.error(`🔴 [ActivityService] CRITICAL: Spelling exercise had mistakes but was incorrectly marked perfect - correcting!`);
            activity.isPerfect = false;
            
            // Remove perfect level bonus if it was incorrectly applied
            if (activity.perfectLevelBonus) {
              const previousXp = activity.xpEarned;
              activity.xpEarned = activity.baseXpEarned || activity.xpEarned;
              activity.perfectLevelBonus = 0;
              console.log(`🔄 [ActivityService] Removed perfect bonus, adjusted XP from ${previousXp} to ${activity.xpEarned}`);
            }
          }
        } catch (e) {
          console.error('Error validating spelling exercise perfect status', e);
        }
      }
      
      // Handle perfect level completion bonuses
      if (activity.type === 'level-completed' && activity.isPerfect === true) {
        // Default to 5 if not specified but the level was perfect
        if (activity.perfectLevelBonus === undefined || activity.perfectLevelBonus === 0) {
          console.warn('Perfect level without bonus specified, defaulting to 5');
          activity.perfectLevelBonus = 5;
        }
        
        // Ensure total XP is correctly calculated for perfect levels
        if (activity.baseXpEarned && activity.perfectLevelBonus) {
          // Store original values for debugging
          const originalXpEarned = activity.xpEarned;
          const baseXp = activity.baseXpEarned;
          const bonus = activity.perfectLevelBonus;
          
          // Update xpEarned to be the sum of base + bonus
          activity.xpEarned = baseXp + bonus;
          
          console.log(`📝 [ActivityService] Updating xpEarned for perfect level: ${baseXp} base + ${bonus} bonus = ${activity.xpEarned} total (was: ${originalXpEarned}`);
        }
        
        console.log(`📝 [ActivityService] Logging perfect level with bonus: ${activity.perfectLevelBonus}, total XP: ${activity.xpEarned}`);
      }
      
      // Prepare the activity log with timestamp
      const activityData = {
        ...activity,
        timestamp: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, this.collection), activityData);
      
      return docRef.id;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error logging activity:', firestoreError.code, firestoreError.message);
      toast.error('Failed to log activity');
      return null;
    }
  }

  // Get activity logs for a user
  async getUserActivities(userId: string): Promise<ActivityLog[]> {
    try {
      
      // Use a compound query to enforce security on the client side
      // We specifically filter by userId
      const q = query(
        collection(db, this.collection),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Comprehensive debug logging for lesson-completed activities
        if (data.type === 'lesson-completed') {
          console.debug('Found lesson-completed activity with raw data:', {
            ...data,
            // Extract all properties explicitly for debugging
            isPerfectLesson: data.isPerfectLesson,
            perfectLessonBonus: data.perfectLessonBonus,
            // Show all keys in the data object
            allKeys: Object.keys(data)
          });
        }
        
        // Create the activity object with all possible fields
        return {
          id: doc.id,
          userId: data.userId,
          timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
          type: data.type,
          lessonId: data.lessonId,
          lessonName: data.lessonName,
          levelIndex: data.levelIndex,
          levelType: data.levelType,
          xpEarned: data.xpEarned,
          baseXpEarned: data.baseXpEarned,
          isPerfect: data.isPerfect,
          // Make sure we capture isPerfectLesson correctly
          isPerfectLesson: data.isPerfectLesson === true,
          perfectLessonBonus: data.perfectLessonBonus,
          perfectLevelBonus: data.perfectLevelBonus
        };
      });
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error fetching activity logs:', firestoreError.code, firestoreError.message);
      
      // Provide more specific error message for better debugging
      if (firestoreError.code === 'permission-denied') {
        toast.error('Access denied: Check Firestore security rules');
        console.error('This is likely a Firestore security rules issue. Make sure your rules allow querying activityLogs where userId == auth.uid');
      } else {
        toast.error('Failed to load activity history');
      }
      
      return [];
    }
  }
}

export const activityService = new ActivityService();
